/**
 * Shared focus-trap utilities for modal overlays.
 *
 * Keeps Tab/Shift+Tab cycling inside a dialog container. Consumers attach a
 * keydown handler on the dialog panel and call `trapFocus` when the key is
 * "Tab". The trap only intercepts keys dispatched INSIDE the container —
 * initial focus must be placed within the dialog by the consumer or the page
 * behind stays focused (see Dialog.tsx / PlexCredentialModal.tsx usage).
 *
 * Hand-rolled overlay dialogs should not wire this themselves: `useDialogFocus`
 * owns initial focus, Escape, this trap and focus return.
 */

/** The slice of a keyboard event the trap reads; React and native events both satisfy it. */
type TabKeyEvent = { shiftKey: boolean; preventDefault: () => void };

export const FOCUSABLE_SELECTOR =
  "button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";

const isRadio = (el: HTMLElement): el is HTMLInputElement => (el as HTMLInputElement).type === "radio";

const isRendered = (el: HTMLElement): boolean =>
  el.closest("[hidden], [inert]") === null && el.getClientRects().length > 0;

/**
 * The elements Tab can actually stop on, in DOM order: focusable, not `tabindex="-1"`,
 * rendered, and one stop per radio group (the checked radio, else the group's first).
 */
export const getTabStops = (container: Pick<HTMLElement, "querySelectorAll">): HTMLElement[] => {
  const candidates = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => el.getAttribute("tabindex") !== "-1" && isRendered(el),
  );
  const groupStop = new Map<string, HTMLElement>();
  for (const el of candidates) {
    if (!isRadio(el) || !el.name) continue;
    const current = groupStop.get(el.name);
    if (!current || (el.checked && !(current as HTMLInputElement).checked)) groupStop.set(el.name, el);
  }
  return candidates.filter((el) => !isRadio(el) || !el.name || groupStop.get(el.name) === el);
};

/**
 * Records the element that held focus when a dialog opened and returns the function that
 * hands focus back to it on close, skipping an opener that has left the document.
 */
export const rememberOpener = (opener: Element | null): (() => void) => () => {
  if (opener && opener.isConnected && typeof (opener as HTMLElement).focus === "function") {
    (opener as HTMLElement).focus();
  }
};

export const trapFocus = (
  container: Pick<HTMLElement, "querySelectorAll">,
  event: TabKeyEvent,
  active: Element | null = document.activeElement,
): void => {
  const focusable = getTabStops(container);
  if (focusable.length === 0) return;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  if (event.shiftKey && active === first) {
    event.preventDefault();
    last?.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first?.focus();
  }
};
